// admin/conteudo.js — toggles de visibilidade do portal (cursos, seções, avisos).
//
// Origem (monólito): linhas 6148-6213 (carregarConfPortal, salvarConfPortal,
//   aplicarConfPortal) e 6213-6224 (iniciarAdminConteudo).
//
// Dependências:
//   - storage (core/storage.js) — persistência em 'config:portal'
//   - notify  (core/notify.js)  — feedback visual ao salvar
//   - router  (core/router.js)  — botão voltar
//
// Exporta:
//   function render(container)
//
// Notas v19:
//   - Chave normalizada: 'config:portal' (storage.js prefixará 'eloq:').
//   - CSS dos toggles já importado via src/styles/components/admin-conteudo.css.
//   - aplicarConfPortal() foi intencionalmente NÃO incluída aqui — ela altera
//     a UI do portal do aluno que corre numa rota diferente. O efeito real
//     ocorrerá quando o aluno carregar /aluno (lê de storage). Deixado como
//     PENDÊNCIA de Fase 8 para sincronização cross-rota.

import { storage } from '../core/storage.js';
import { notify }  from '../core/notify.js';
import { router }  from '../core/router.js';

// ── Chave de storage ──────────────────────────────────────────────────────────

const CONF_KEY = 'config:portal';

// ── Configuração padrão ───────────────────────────────────────────────────────

const CONF_DEFAULT = {
  cursosSection: true,
  cursos: {
    'oratoria-power': true,
    persuasao: true,
    corporativo: true,
    palestras: true,
  },
  aviso: { ativo: false, tipo: 'info', texto: '' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function lerConf() {
  const salvo = storage.get(CONF_KEY, null);
  if (!salvo) return JSON.parse(JSON.stringify(CONF_DEFAULT));
  // Mescla para garantir que chaves novas do default estejam presentes
  return {
    ...CONF_DEFAULT,
    ...salvo,
    cursos: { ...CONF_DEFAULT.cursos, ...(salvo.cursos || {}) },
    aviso:  { ...CONF_DEFAULT.aviso,  ...(salvo.aviso  || {}) },
  };
}

function salvarConf(conf) {
  return storage.set(CONF_KEY, conf);
}

// ── Helpers de HTML ───────────────────────────────────────────────────────────

/**
 * Gera HTML de uma linha de toggle.
 * @param {string} id       - id do input checkbox
 * @param {string} icone    - emoji
 * @param {string} rotulo   - texto principal
 * @param {string} sub      - texto auxiliar
 * @param {boolean} checked - estado inicial
 */
function _toggleRow(id, icone, rotulo, sub, checked) {
  return `
    <div class="admin-toggle-row">
      <div>
        <div class="admin-toggle-label">${icone} ${rotulo}</div>
        ${sub ? `<div class="admin-toggle-sub">${sub}</div>` : ''}
      </div>
      <label class="toggle-switch">
        <input type="checkbox" id="${id}" ${checked ? 'checked' : ''}>
        <span class="toggle-slider"></span>
      </label>
    </div>`;
}

// ── Render principal ──────────────────────────────────────────────────────────

export function render(container) {
  const conf = lerConf();

  container.innerHTML = `
    <div style="min-height:100vh;background:#F5F5F5;font-family:'Montserrat',sans-serif;">
      <header style="background:linear-gradient(90deg,#6A001F,#B3002D);padding:calc(env(safe-area-inset-top,0px)+16px) 20px 16px;position:sticky;top:0;z-index:100">
        <div style="display:flex;align-items:center;gap:14px">
          <button id="conteudoBackBtn" style="width:38px;height:38px;border-radius:50%;border:none;background:rgba(255,255,255,0.18);color:#fff;font-size:18px;cursor:pointer;flex-shrink:0">←</button>
          <div>
            <div style="font-size:10px;font-weight:600;color:rgba(255,255,255,0.65);letter-spacing:2px;text-transform:uppercase">Admin</div>
            <div style="font-size:20px;font-weight:800;color:#fff">Gerenciar Conteúdo</div>
          </div>
        </div>
      </header>

      <main style="padding:20px;padding-bottom:40px">

        <!-- Seção de cursos -->
        <div class="admin-content-panel">
          <div class="admin-content-title">🎓 Seção de Cursos</div>
          ${_toggleRow('toggleCursosSection', '📚', 'Exibir seção "Nossos Cursos"', 'Oculta toda a seção do carrossel no portal', conf.cursosSection)}
          <div id="cursosIndividuais" style="${conf.cursosSection ? '' : 'opacity:0.4;pointer-events:none'}">
            ${_toggleRow('toggleCurso_oratoria-power', '🎤', 'Oratória Power', 'Card do curso de Oratória no carrossel', conf.cursos['oratoria-power'])}
            ${_toggleRow('toggleCurso_persuasao', '🧠', 'Persuasão', 'Card do curso de Persuasão', conf.cursos['persuasao'])}
            ${_toggleRow('toggleCurso_corporativo', '💼', 'Corporativo', 'Card do curso Corporativo', conf.cursos['corporativo'])}
            ${_toggleRow('toggleCurso_palestras', '🎙️', 'Palestras', 'Card do curso de Palestras', conf.cursos['palestras'])}
          </div>
        </div>

        <!-- Aviso do portal -->
        <div class="admin-content-panel">
          <div class="admin-content-title">📢 Aviso no Portal</div>
          ${_toggleRow('toggleAviso', '🔔', 'Exibir banner de aviso', 'Banner informativo no topo do portal do aluno', conf.aviso.ativo)}

          <div id="avisoConfig" style="${conf.aviso.ativo ? '' : 'display:none'}">
            <div style="margin-top:12px">
              <div style="font-size:11px;font-weight:700;color:#9B9B9B;text-transform:uppercase;letter-spacing:1px;margin-bottom:8px">Tipo do aviso</div>
              <div style="display:flex;gap:8px;flex-wrap:wrap">
                ${[
                  { val:'info',    emoji:'📢', label:'Info',    cor:'#3b82f6' },
                  { val:'success', emoji:'🎉', label:'Sucesso', cor:'#34c759' },
                  { val:'warning', emoji:'⚠️', label:'Aviso',   cor:'#f5a623' },
                ].map(op => `
                  <label style="display:flex;align-items:center;gap:6px;cursor:pointer;
                    padding:8px 14px;border-radius:100px;
                    border:1.5px solid ${conf.aviso.tipo === op.val ? op.cor : '#E8E8E8'};
                    background:${conf.aviso.tipo === op.val ? op.cor + '1A' : '#fff'};
                    font-size:12px;font-weight:700;color:#0A0A0A;transition:all 0.18s">
                    <input type="radio" name="avisoTipo" value="${op.val}"
                      ${conf.aviso.tipo === op.val ? 'checked' : ''}
                      style="display:none">
                    ${op.emoji} ${op.label}
                  </label>`).join('')}
              </div>
            </div>
            <textarea
              id="avisoTexto"
              class="admin-aviso-textarea"
              placeholder="Digite o texto do aviso que aparecerá para os alunos..."
            >${conf.aviso.texto}</textarea>
          </div>
        </div>

        <!-- Botão salvar -->
        <button class="admin-save-btn" id="conteudoSalvarBtn">✓ Salvar configurações</button>

        <!-- Info -->
        <div style="margin-top:16px;padding:12px 16px;background:#fff3e0;border-radius:12px;font-size:12px;color:#7a4f00;font-weight:600">
          ⚠️ As alterações terão efeito na próxima vez que um aluno abrir o portal.
          Sincronização em tempo real virá em versão futura.
        </div>
      </main>
    </div>`;

  // ── Eventos ──────────────────────────────────────────────────────────────────

  container.querySelector('#conteudoBackBtn').addEventListener('click', () => router.go('/admin'));

  // Seção de cursos: toggle principal controla sub-opções
  const toggleCursosSection = container.querySelector('#toggleCursosSection');
  const cursosIndividuais   = container.querySelector('#cursosIndividuais');
  toggleCursosSection.addEventListener('change', () => {
    cursosIndividuais.style.opacity        = toggleCursosSection.checked ? '1' : '0.4';
    cursosIndividuais.style.pointerEvents  = toggleCursosSection.checked ? '' : 'none';
  });

  // Aviso: toggle controla visibilidade das opções de tipo/texto
  const toggleAviso  = container.querySelector('#toggleAviso');
  const avisoConfig  = container.querySelector('#avisoConfig');
  toggleAviso.addEventListener('change', () => {
    avisoConfig.style.display = toggleAviso.checked ? '' : 'none';
  });

  // Clique nos labels de tipo de aviso para estilizar seleção
  container.querySelectorAll('input[name="avisoTipo"]').forEach(radio => {
    radio.addEventListener('change', () => {
      const COR_MAP = { info:'#3b82f6', success:'#34c759', warning:'#f5a623' };
      container.querySelectorAll('label[style*="border"]').forEach(label => {
        const r = label.querySelector('input[name="avisoTipo"]');
        if (!r) return;
        const ativo = r.value === radio.value;
        const cor = COR_MAP[r.value] || '#E8E8E8';
        label.style.borderColor = ativo ? cor : '#E8E8E8';
        label.style.background  = ativo ? cor + '1A' : '#fff';
      });
    });
  });

  // Salvar
  container.querySelector('#conteudoSalvarBtn').addEventListener('click', () => {
    const novaConf = {
      cursosSection: container.querySelector('#toggleCursosSection').checked,
      cursos: {
        'oratoria-power': container.querySelector('#toggleCurso_oratoria-power').checked,
        persuasao:        container.querySelector('#toggleCurso_persuasao').checked,
        corporativo:      container.querySelector('#toggleCurso_corporativo').checked,
        palestras:        container.querySelector('#toggleCurso_palestras').checked,
      },
      aviso: {
        ativo: container.querySelector('#toggleAviso').checked,
        tipo:  (container.querySelector('input[name="avisoTipo"]:checked') || { value: 'info' }).value,
        texto: container.querySelector('#avisoTexto').value.trim(),
      },
    };

    if (salvarConf(novaConf)) {
      notify.success('Configurações salvas com sucesso!');
      const btn = container.querySelector('#conteudoSalvarBtn');
      btn.textContent = '✓ Salvo!';
      setTimeout(() => { btn.textContent = '✓ Salvar configurações'; }, 1500);
    } else {
      notify.error('Falha ao salvar configurações.');
    }
  });
}
